import React from 'react';
import { Typography, FormControlLabel, Switch, IconButton } from '@mui/material';
import { MainContentPanel } from '../../components/MainContentPanel';
import Loading from '../../components/LoadingSpinner';

import { GET_DISCOUNTS } from '../discount.graphql';
import { SearchField } from '../../components/SearchField';
import { useState } from 'react';
import Fuse from 'fuse.js';
import { formatDateOnly, numberAsKr, numberAsPercentage } from '../../Formatters';
import { Link, useParams } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { DiscountOverview } from '../../interfaces';
import { Counter } from '../../components/Counter';
import { DiscountType } from '../../interfaces.enums';
import { useQuery } from '@apollo/client/react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

export function Discounts() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const queryTask = useQuery<{ discounts: DiscountOverview[] }>(GET_DISCOUNTS, { variables: { id } });

  return (
    <MainContentPanel>
      <Typography gutterBottom variant="h5">
        <span>Rabatter</span>
        <Link to="/discount/new">
          <IconButton size="large">
            <Add />
          </IconButton>
        </Link>
      </Typography>
      <Content task={queryTask} />
    </MainContentPanel>
  );
}

function Content({ task }) {
  const { loading, error, data } = task;
  if (loading) return <Loading />;
  if (error) return <Typography>{error}</Typography>;

  return <DiscountSearchableTable discounts={data.discounts} />;
}

function DiscountSearchableTable({ discounts }: { discounts: DiscountOverview[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const filteredDiscounts = filterDiscounts(discounts, searchTerm, onlyActive);
  return (
    <div>
      <SearchField filter={setSearchTerm} />
      <FormControlLabel control={<Switch onChange={() => setOnlyActive(!onlyActive)} checked={onlyActive} />} label="Kun ledige" />
      <Counter list={filteredDiscounts} />
      <DiscountTable discounts={filteredDiscounts} />
    </div>
  );
}

function filterDiscounts(discounts: DiscountOverview[], searchTerm: string, onlyActive: boolean) {
  let semiFiltered = onlyActive ? discounts.filter((x) => x.isApplicable) : discounts;
  var options = {
    shouldSort: false,
    tokenize: true,
    matchAllTokens: true,
    threshold: 0,
    location: 0,
    distance: 10,
    maxPatternLength: 32,
    minMatchCharLength: 3,
    keys: ['name', 'emailFilter'],
  };
  if (!searchTerm) {
    return semiFiltered;
  } else {
    var fuse = new Fuse(semiFiltered, options);
    var filtered = fuse.search(searchTerm).map((r: any) => r.item);
    return filtered;
  }
}

interface DiscountTableProps {
  discounts: DiscountOverview[];
}

function DiscountTable({ discounts }: DiscountTableProps) {
  const columns: GridColDef<DiscountOverview>[] = [
    {
      field: 'name',
      headerName: 'Navn',
      minWidth: 140,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <Link to={'/discount/' + params.row.id}>{params.row.name}</Link>,
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 140,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => (row.type === DiscountType.Percentage ? 'Prosent' : 'Fastpris'),
    },
    {
      field: 'value',
      headerName: 'Rabatt',
      minWidth: 140,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <span>{params.row.type === DiscountType.Percentage ? numberAsPercentage(params.row.value) : numberAsKr(params.row.value)}</span>,
    },
    {
      field: 'validFrom',
      headerName: 'Gyldig fra',
      minWidth: 140,
      flex: 1,
      type: 'date',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => (row.validFrom ? new Date(row.validFrom) : null),
      renderCell: (params) => <span>{formatDateOnly(params.row.validFrom)}</span>,
    },
    {
      field: 'validTo',
      headerName: 'Gyldig til',
      minWidth: 140,
      flex: 1,
      type: 'date',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => (row.validTo ? new Date(row.validTo) : null),
      renderCell: (params) => <span>{formatDateOnly(params.row.validTo)}</span>,
    },
    {
      field: 'emailFilter',
      headerName: 'E-post-filter',
      minWidth: 140,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => row.emailFilter ?? '',
    },
    {
      field: 'usages',
      headerName: 'Brukt av',
      minWidth: 140,
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
    },
  ];

  return (
    <DataGridPro
      autoHeight
      density="compact"
      rows={discounts}
      columns={columns}
      getRowId={(row) => row.id}
      localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
      pagination
      pageSizeOptions={[25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 50,
            page: 0,
          },
        },
      }}
      sx={{
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
      }}
    />
  );
}
